/**
 * Обложки материалов блога.
 *
 * Картинок у статей нет и в ближайшее время не будет: снимать или покупать
 * иллюстрацию к каждому разбору рынка никто не станет. Поэтому обложка
 * рисуется — слоями градиентов из палитры сайта, детерминированно по номеру
 * материала. Один и тот же материал всегда выглядит одинаково, а соседние по
 * списку — по-разному.
 *
 * Два набора: `card` для плитки в ленте и `hero` для шапки самой статьи. Мотив
 * и семейство цвета там разные намеренно — открыв материал, читатель должен
 * видеть не увеличенную копию плитки, а другую картинку.
 *
 * Всё считается на CSS-градиентах: ни файлов, ни запросов, ни веса в сборке.
 */

/** Фиолетовое семейство — основное для сайта. */
const VIOLET = ['#8944EB', '#A068EF', '#6817DA', '#4E11A3', '#BE90FE'] as const

/** Лаймовое — акцентное, встречается на трети материалов. */
const LIME = ['#B8F500', '#D7FF60', '#8FBF00', '#5E7F00', '#E4FF9E'] as const

/** Тёмная подложка: обложка не должна светиться на всю плитку. */
const BASE = '#0A0B11'

type Variant = 'card' | 'hero'

/** Смешать цвет с прозрачностью: обложка приглушённая, а не кислотная. */
const alpha = (hex: string, value: number): string => {
  const n = Number.parseInt(hex.slice(1), 16)

  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${value})`
}

/**
 * Шесть мотивов. Каждый получает два цвета семейства и собирает свой набор
 * слоёв — от мягкого свечения до диагональных полос.
 */
const MOTIFS = [
  // Свечение из двух углов
  (a: string, b: string) => [
    `radial-gradient(120% 90% at 12% 8%, ${alpha(a, 0.85)} 0%, transparent 60%)`,
    `radial-gradient(100% 80% at 88% 96%, ${alpha(b, 0.7)} 0%, transparent 62%)`,
    BASE,
  ],

  // Диагональные полосы
  (a: string, b: string) => [
    `repeating-linear-gradient(115deg, ${alpha(a, 0.5)} 0 14px, transparent 14px 40px)`,
    `linear-gradient(135deg, ${alpha(b, 0.75)} 0%, ${BASE} 78%)`,
    BASE,
  ],

  // Концентрические кольца
  (a: string, b: string) => [
    `repeating-radial-gradient(circle at 78% 22%, ${alpha(a, 0.42)} 0 3px, transparent 3px 26px)`,
    `radial-gradient(90% 120% at 78% 22%, ${alpha(b, 0.8)} 0%, transparent 68%)`,
    BASE,
  ],

  // Сетчатый градиент из трёх пятен
  (a: string, b: string) => [
    `radial-gradient(60% 60% at 22% 78%, ${alpha(a, 0.8)} 0%, transparent 64%)`,
    `radial-gradient(55% 55% at 76% 34%, ${alpha(b, 0.75)} 0%, transparent 62%)`,
    `radial-gradient(70% 70% at 50% 110%, ${alpha(a, 0.45)} 0%, transparent 60%)`,
    BASE,
  ],

  // Широкие косые ленты
  (a: string, b: string) => [
    `linear-gradient(160deg, transparent 0 38%, ${alpha(a, 0.65)} 38% 56%, transparent 56%)`,
    `linear-gradient(160deg, transparent 0 62%, ${alpha(b, 0.5)} 62% 72%, transparent 72%)`,
    `linear-gradient(200deg, ${alpha(b, 0.4)} 0%, ${BASE} 70%)`,
    BASE,
  ],

  // Тонкая сетка поверх свечения
  (a: string, b: string) => [
    `repeating-linear-gradient(0deg, ${alpha(a, 0.28)} 0 1px, transparent 1px 22px)`,
    `repeating-linear-gradient(90deg, ${alpha(a, 0.28)} 0 1px, transparent 1px 22px)`,
    `radial-gradient(100% 100% at 30% 20%, ${alpha(b, 0.85)} 0%, transparent 70%)`,
    BASE,
  ],
] as const

/**
 * Заливка обложки.
 *
 * Мотив и семейство цвета сдвинуты для шапки статьи относительно плитки:
 * при одном и том же номере материала это гарантированно разные картинки.
 */
export const blogCover = (id: number, variant: Variant = 'card'): string => {
  const safeId = Number.isFinite(id) ? Math.abs(Math.trunc(id)) : 0
  const shift = variant === 'hero' ? 3 : 0

  // Лайм достаётся каждому третьему материалу, и в шапке смещение меняет
  // семейство: плитка фиолетовая — шапка лаймовая, и наоборот.
  const family = (safeId + shift) % 3 === 0 ? LIME : VIOLET
  const motif = MOTIFS[(safeId + shift) % MOTIFS.length]!

  const a = family[safeId % family.length]!
  const b = family[(safeId + 2) % family.length]!

  return motif(a, b).join(', ')
}

/**
 * Адрес обложки, загруженной в кабинете.
 *
 * Strapi отдаёт относительный путь вида `/uploads/…`, а сайт живёт на другом
 * домене — без основы такая ссылка ведёт в никуда. Внешнюю ссылку, вставленную
 * руками, оставляем как есть.
 */
export const blogCoverUrl = (cover: string | null | undefined, apiUrl: string): string => {
  const value = String(cover ?? '').trim()

  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value

  // apiUrl оканчивается на /api — до файлов из него нужен корень
  return `${apiUrl.replace(/\/api\/?$/, '')}${value.startsWith('/') ? value : `/${value}`}`
}
